import { Injectable, signal, computed } from '@angular/core';
import { Observable, from, of, catchError, map, tap } from 'rxjs';
import { GeolocationStatus } from '../enums/geolocation-status.enum';
import { UserLocationV3 } from '../interfaces/store-v3';

/**
 * Servicio para manejar la geolocalización del usuario
 *
 * Este servicio:
 * 1. Solicita permisos de ubicación al usuario
 * 2. Obtiene la posición actual (latitud/longitud)
 * 3. Maneja diferentes estados (aceptado, rechazado, no soportado)
 *
 * CÓMO USAR:
 * ```typescript
 * // En tu componente
 * constructor(private geolocationService: GeolocationV3Service) {}
 *
 * ngOnInit() {
 *   // Solicitar permiso (retorna Observable)
 *   this.geolocationService.requestPermission().subscribe(granted => {
 *     if (granted) {
 *       const location = this.geolocationService.location();
 *       console.log(location?.latitude, location?.longitude);
 *     }
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class GeolocationV3Service {

  // ============================================================
  // SIGNALS - Estado reactivo del servicio
  // ============================================================

  /**
   * Ubicación actual del usuario
   * Será null si no se ha obtenido o el usuario rechazó el permiso
   */
  private _location = signal<UserLocationV3 | null>(null);

  /**
   * Estado actual de la geolocalización
   */
  private _status = signal<GeolocationStatus>(GeolocationStatus.instructions);

  /**
   * Indica si estamos en proceso de obtener la ubicación
   */
  private _isLoading = signal<boolean>(false);

  // ============================================================
  // SIGNALS PÚBLICOS (solo lectura)
  // ============================================================

  /**
   * Ubicación del usuario (lectura)
   * Ejemplo: { latitude: 9.0, longitude: -79.5, granted: true }
   */
  readonly location = computed(() => this._location());

  /**
   * Estado de la geolocalización (lectura)
   * Valores: 'instructions', 'accepted', 'not-accepted', 'not-support'
   */
  readonly status = computed(() => this._status());

  /**
   * ¿Está cargando? (lectura)
   */
  readonly isLoading = computed(() => this._isLoading());

  /**
   * ¿El usuario dio permiso de ubicación? (lectura)
   * Retorna true si tenemos ubicación válida
   */
  readonly hasLocation = computed(() => {
    const loc = this._location();
    return loc !== null && loc.granted === true;
  });

  // ============================================================
  // MÉTODOS PÚBLICOS - OBSERVABLES
  // ============================================================

  /**
   * Solicita permiso de geolocalización al usuario
   *
   * FLUJO:
   * 1. Verifica si el navegador soporta geolocalización
   * 2. Verifica el estado del permiso (granted, denied, prompt)
   * 3. Si es necesario, muestra el prompt al usuario
   * 4. Si el usuario acepta, obtiene la ubicación
   *
   * @returns Observable<boolean> - true si se obtuvo la ubicación
   */
  requestPermission(): Observable<boolean> {
    this._isLoading.set(true);

    // Paso 1: Verificar si el navegador soporta geolocalización
    if (!this.isGeolocationSupported()) {
      console.warn('[GeolocationV3Service] El navegador no soporta geolocalización');
      this._status.set(GeolocationStatus['not-support']);
      this._isLoading.set(false);
      return of(false);
    }

    // Paso 2: Verificar el estado actual del permiso y obtener ubicación
    return from(this.checkPermissionAndGetLocation()).pipe(
      tap(() => this._isLoading.set(false)),
      catchError(error => {
        console.error('[GeolocationV3Service] Error:', error);
        this._isLoading.set(false);
        this._status.set(GeolocationStatus['not-support']);
        return of(false);
      })
    );
  }

  /**
   * Obtiene la posición actual del usuario
   *
   * NOTA: Este método mostrará el prompt del navegador si aún no se ha dado permiso
   *
   * @returns Observable<UserLocationV3 | null> - Ubicación o null si falló
   */
  getCurrentPosition(): Observable<UserLocationV3 | null> {
    return from(this.getPositionPromise()).pipe(
      map(location => {
        if (location) {
          this._location.set(location);
          this._status.set(GeolocationStatus.accepted);
        }
        return location;
      }),
      catchError(error => {
        console.error('[GeolocationV3Service] Error al obtener posición:', error);
        this._location.set(null);
        this._status.set(GeolocationStatus['not-accepted']);
        return of(null);
      })
    );
  }

  /**
   * Calcula la distancia entre dos puntos geográficos
   *
   * Usa la fórmula de Haversine para calcular la distancia
   * en línea recta (no por carretera) entre dos coordenadas
   *
   * @param lat1 - Latitud del punto 1
   * @param lon1 - Longitud del punto 1
   * @param lat2 - Latitud del punto 2
   * @param lon2 - Longitud del punto 2
   * @returns Distancia en kilómetros
   *
   * EJEMPLO:
   * ```typescript
   * // Distancia entre Ciudad de Panamá y Colón
   * const distance = this.calculateDistance(9.0, -79.5, 9.35, -79.9);
   * console.log(distance); // Aproximadamente 48 km
   * ```
   */
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const EARTH_RADIUS_KM = 6371; // Radio de la Tierra en kilómetros

    // Convertir grados a radianes
    const toRadians = (degrees: number) => degrees * (Math.PI / 180);

    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const lat1Rad = toRadians(lat1);
    const lat2Rad = toRadians(lat2);

    // Fórmula de Haversine
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1Rad) * Math.cos(lat2Rad) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_KM * c;
  }

  /**
   * Resetea el estado del servicio
   * Útil para cuando el usuario quiere reintentar
   */
  reset(): void {
    this._location.set(null);
    this._status.set(GeolocationStatus.instructions);
    this._isLoading.set(false);
  }

  // ============================================================
  // MÉTODOS PRIVADOS
  // ============================================================

  /**
   * Verifica si el navegador soporta geolocalización
   */
  private isGeolocationSupported(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Verifica el permiso y obtiene la ubicación
   * @returns Promise<boolean>
   */
  private async checkPermissionAndGetLocation(): Promise<boolean> {
    try {
      // Verificar estado del permiso si la API está disponible
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });

        switch (permission.state) {
          case 'granted':
            console.log('[GeolocationV3Service] Permiso ya otorgado');
            break;
          case 'denied':
            console.log('[GeolocationV3Service] Permiso denegado');
            this._status.set(GeolocationStatus['not-accepted']);
            return false;
          case 'prompt':
            console.log('[GeolocationV3Service] Solicitando permiso...');
            this._status.set(GeolocationStatus.instructions);
            break;
        }
      }

      // Intentar obtener la ubicación
      const location = await this.getPositionPromise();

      if (location) {
        this._location.set(location);
        this._status.set(GeolocationStatus.accepted);
        return true;
      }

      return false;

    } catch (error) {
      console.error('[GeolocationV3Service] Error:', error);
      this._status.set(GeolocationStatus['not-accepted']);
      return false;
    }
  }

  /**
   * Envuelve la API de geolocalización del navegador en una Promise
   * @returns Promise<UserLocationV3 | null>
   */
  private getPositionPromise(): Promise<UserLocationV3 | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        this._status.set(GeolocationStatus['not-support']);
        resolve(null);
        return;
      }

      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Cache de 5 minutos
      };

      navigator.geolocation.getCurrentPosition(
        // ÉXITO
        (position) => {
          const location: UserLocationV3 = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: position.timestamp,
            granted: true
          };
          console.log('[GeolocationV3Service] Ubicación obtenida:', location);
          resolve(location);
        },
        // ERROR
        (error) => {
          console.error('[GeolocationV3Service] Error:', error.message);

          switch (error.code) {
            case error.PERMISSION_DENIED:
              this._status.set(GeolocationStatus['not-accepted']);
              break;
            default:
              this._status.set(GeolocationStatus['not-support']);
              break;
          }

          resolve(null);
        },
        options
      );
    });
  }
}